import { useCallback } from 'react'
import {
  createActivityEffect,
  updateActivityEffect,
  createActivityBulkEffect,
  updateActivityBulkEffect,
} from '../../../effects'
import { useActivitiesContext } from '../../../context'
import { useIsUnmounted } from '@sales-center/components'
import type { ActivitiesQuantity, Activity } from '../../../typings'
import { set, getHours, getMinutes } from 'date-fns'
import { ENTITY_TYPES } from '@sales-center/common'
import { useRefreshActivityHistory } from '@user/features/Activity/hooks/useRefreshActivityHistory'
import type { Actions } from '../../../state'
import { useAppDispatch } from '@user/store/hooks'
import { fetchOverdueActivitiesCount } from '@user/features/Activity/store'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { ActivityForm } from '../typings'
import {
  bulkSetNextActionDate as leadBulkSetNextActionDate,
  setNextActionDate as leadSetNextActionDate,
} from '@user/store/lead'
import {
  bulkSetNextActionDate as oppBulkSetNextActionDate,
  setNextActionDate as oppSetNextActionDate,
  setExpectedClosingDate,
  bulkSetExpectedClosingDate,
} from '@user/store/opportunity'

function prepareData(
  model: ActivityForm,
  entityIdOrIds: number | number[] | undefined | null,
  entityType: ENTITY_TYPES | undefined | null,
  activitiesQuantity?: 'single' | 'bulk',
  hasEntity = entityIdOrIds != null && entityType != null,
): Activity {
  const { dueDate, dueTime, startDate, startTime, ownerId, ...createModel } =
    model

  const entityIdKey = activitiesQuantity === 'bulk' ? 'entityIds' : 'entityId'

  // SH-2338: an activity can be created with no lead or opportunity. Send
  // explicit nulls rather than undefined so the intent is unambiguous on the
  // wire, and suppress the entity-date flags — with no record to write them to
  // the server ignores them, and their checkboxes are hidden in this state.
  return {
    ...createModel,
    [entityIdKey]: entityIdOrIds ?? null,
    ownerId: ownerId === '-1' || ownerId === 'unassigned' ? null : ownerId,
    entityType: entityType ?? null,
    setDueDateAsNextActionDate: hasEntity
      ? createModel.setDueDateAsNextActionDate
      : false,
    setDueDateAsExpectedClosingDate: hasEntity
      ? createModel.setDueDateAsExpectedClosingDate
      : false,
    dueDate: dueDate
      ? set(new Date(dueDate), {
          hours: getHours(dueTime),
          minutes: getMinutes(dueTime),
        })
      : null,
    startDate: startDate
      ? set(startDate, {
          hours: getHours(startTime),
          minutes: getMinutes(startTime),
        })
      : null,
    // The Activity type declares dueDate/startDate as string and entityType as a
    // capitalized union, but the create payload legitimately carries Date values
    // and the ENTITY_TYPES enum (serialized on the wire) — assert the real shape.
  } as unknown as Activity
}

type Props =
  | {
      entityId: number
      entityType: ENTITY_TYPES
      activitiesQuantity: 'single'
      // SH-1428: calendar "Create Event" — entity comes from the Attach-To
      // picker (Formik values) rather than a pre-selected lead/opp.
      attachToMode?: boolean
      // Called after a successful create (used by the calendar dialog to close
      // itself and trigger the SH-1408 refetch seam).
      onSuccess?: () => void
    }
  | {
      entityId: number[]
      entityType: ENTITY_TYPES
      activitiesQuantity: 'bulk'
      attachToMode?: boolean
      onSuccess?: () => void
    }

const activityActions: Record<
  ActivitiesQuantity,
  Record<
    'create' | 'update',
    (activity: Activity | Omit<Activity, 'id'>) => Promise<Actions | undefined>
  >
> = {
  single: {
    create: createActivityEffect,
    update: updateActivityEffect,
  },
  bulk: {
    create: createActivityBulkEffect,
    update: updateActivityBulkEffect,
  },
}

export function useHandlers(props: Props) {
  const { entityId, entityType, activitiesQuantity, attachToMode, onSuccess } =
    props
  const [{ current }, dispatch] = useActivitiesContext()
  const appDispatch = useAppDispatch()

  const { userId } = useSelector(selectCurrentUser)

  const isUnmounted = useIsUnmounted()
  const { refreshHistory } = useRefreshActivityHistory(
    Array.isArray(entityId) ? entityId[0] : entityId,
    entityType,
  )

  const handleSubmit = useCallback(
    async (
      model: ActivityForm,
      { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
    ) => {
      // SH-1428: in attach mode the entity is chosen in the form (Attach-To
      // picker) and lives on the Formik model; otherwise it comes from props.
      const modelEntity = model as ActivityForm & {
        entityId?: number
        entityType?: ENTITY_TYPES
      }
      const effEntityId = (
        attachToMode ? modelEntity.entityId : entityId
      ) as typeof entityId
      const effEntityType = (
        attachToMode ? modelEntity.entityType : entityType
      ) as ENTITY_TYPES

      // SH-2338: one predicate for "is there a record to write entity dates to",
      // used by both the wire payload (prepareData) and the local store update
      // below. They previously decided it two different ways — this one off
      // hasEntity, that one off the raw model flag — which happened to agree
      // only because effEntityType is null when unattached and both branches
      // compare against a specific ENTITY_TYPES member. That is an accidental
      // invariant: ActivityForm already defaults entityType to Lead one layer
      // up, so a future change there would have silently reintroduced a store
      // write against an undefined id.
      const hasEntity = effEntityId != null && effEntityType != null

      function updateDatesInStore(model: ActivityForm) {
        if (!hasEntity) return

        if (model.setDueDateAsNextActionDate) {
          if (effEntityType === ENTITY_TYPES.Lead) {
            if (activitiesQuantity === 'single') {
              // entityId is narrowed to number inside the single branch.
              const leadId = attachToMode
                ? (modelEntity.entityId as number)
                : entityId
              appDispatch(
                leadSetNextActionDate({ leadId, date: model.dueDate }),
              )
            } else {
              appDispatch(
                leadBulkSetNextActionDate({
                  leadIds: entityId,
                  date: model.dueDate,
                }),
              )
            }
          }
          if (effEntityType === ENTITY_TYPES.Opportunity) {
            if (activitiesQuantity === 'single') {
              const oppId = attachToMode
                ? (modelEntity.entityId as number)
                : entityId
              appDispatch(oppSetNextActionDate({ oppId, date: model.dueDate }))
            } else {
              appDispatch(
                oppBulkSetNextActionDate({
                  oppIds: entityId,
                  date: model.dueDate,
                }),
              )
            }
          }
        }

        if (
          model.setDueDateAsExpectedClosingDate &&
          effEntityType === ENTITY_TYPES.Opportunity
        ) {
          if (activitiesQuantity === 'single') {
            const oppId = attachToMode
              ? (modelEntity.entityId as number)
              : entityId
            appDispatch(setExpectedClosingDate({ oppId, date: model.dueDate }))
          } else {
            appDispatch(
              bulkSetExpectedClosingDate({
                oppIds: entityId,
                date: model.dueDate,
              }),
            )
          }
        }
      }

      setSubmitting(true)
      const createData: Activity = prepareData(
        model,
        effEntityId,
        effEntityType,
        activitiesQuantity,
        hasEntity,
      )

      const action = current
        ? await activityActions[activitiesQuantity]['update']({
            ...createData,
            id: current,
          })
        : await activityActions[activitiesQuantity]['create'](createData)

      // In attach mode there is no hosted activity-history popover to refresh
      // (the refresh hook was created with the prop entityId, not the selected one).
      if (!attachToMode) refreshHistory()
      await appDispatch(fetchOverdueActivitiesCount(userId))
      if (isUnmounted()) return

      if (action) {
        dispatch(action)
        updateDatesInStore(model)
      }

      setSubmitting(false)

      // Fire last: onSuccess may unmount this form (calendar dialog closes on
      // create), so run it after Formik state is settled.
      if (action) onSuccess?.()
    },
    [
      entityId,
      entityType,
      activitiesQuantity,
      attachToMode,
      onSuccess,
      current,
      refreshHistory,
      appDispatch,
      userId,
      isUnmounted,
      dispatch,
    ],
  )

  return {
    handleSubmit,
  }
}
